import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, Mail, XCircle } from 'lucide-react';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { useUpdateBranding } from '../../hooks/queries/useTenantBranding';
import { useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../lib/supabaseClient';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';

interface FirmFormValues {
  firm_name: string;
}

async function sendTestEmail(to: string): Promise<{ from: string }> {
  const { data, error } = await supabase.functions.invoke('send-test-email', {
    body: { to },
  });
  if (error) {
    // Extract the actual error body from the HTTP response when available
    try {
      const body = await (error as any).context?.json?.();
      throw new Error(body?.error ?? error.message);
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr !== error) throw parseErr;
    }
    throw error;
  }
  if (!data?.success) throw new Error(data?.error ?? "Échec de l'envoi");
  return data as { from: string };
}

export function FirmSettingsTab() {
  const { branding, isLoading } = useTenantBranding();
  const updateBranding = useUpdateBranding();
  const currentEmail = useAuthStore((s) => s.collaborator?.email ?? '');
  const [testEmail, setTestEmail] = useState('');

  const testMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: (data) => {
      toast.success(`Email envoyé depuis ${data.from}`);
    },
    onError: (err: Error) => {
      toast.error(`Erreur : ${err.message}`);
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FirmFormValues>({
    defaultValues: {
      firm_name: branding.firm_name,
    },
  });

  useEffect(() => {
    reset({ firm_name: branding.firm_name });
  }, [branding.firm_name, reset]);

  // Pre-fill test email with current user's email once loaded
  useEffect(() => {
    if (currentEmail && !testEmail) setTestEmail(currentEmail);
  }, [currentEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(values: FirmFormValues) {
    try {
      await updateBranding.mutateAsync({ firm_name: values.firm_name });
      toast.success('Informations du cabinet mises à jour');
      reset(values); // mark form as clean with new values
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <span
          className="w-6 h-6 rounded-full border-2 border-cime-primary border-t-transparent animate-spin"
          aria-label="Chargement en cours"
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2
          className="text-xl font-semibold text-neutral-900"
        >
          Informations du cabinet
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Nom du cabinet affiché dans l'interface et les documents.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Input
            label="Raison sociale"
            placeholder="CIME Avocats"
            error={errors.firm_name?.message}
            {...register('firm_name', {
              required: 'La raison sociale est obligatoire',
              maxLength: {
                value: 200,
                message: '200 caractères maximum',
              },
            })}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting || updateBranding.isPending}
              disabled={!isDirty}
            >
              Enregistrer
            </Button>
          </div>
        </form>
      </Card>

      <p className="text-xs text-neutral-400 leading-relaxed">
        Les informations légales complètes (SIRET, adresse, barreau) seront disponibles en Phase 3
        avec le module de facturation.
      </p>

      {/* ── Email de test ── */}
      <div>
        <h2
          className="text-xl font-semibold text-neutral-900"
        >
          Notifications email
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Vérifiez que la configuration DKIM / SPF est opérationnelle en envoyant un email de test.
        </p>
      </div>

      <Card>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Adresse destinataire"
                type="email"
                placeholder="email@exemple.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <div className="pt-6">
              <Button
                variant="secondary"
                onClick={() => testMutation.mutate(testEmail)}
                disabled={!testEmail || testMutation.isPending}
                isLoading={testMutation.isPending}
              >
                <Mail className="w-4 h-4" />
                Envoyer un test
              </Button>
            </div>
          </div>

          {testMutation.isSuccess && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-cime px-3 py-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Email envoyé avec succès depuis <strong>{testMutation.data.from}</strong>
            </div>
          )}

          {testMutation.isError && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-cime px-3 py-2">
              <XCircle className="w-4 h-4 flex-shrink-0" />
              {(testMutation.error as Error).message}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
